import React from 'react';
import { Box, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { SeasonBadge } from './Badges';
import { displayTitle, thumbTarget } from './format';
import { UNACCOUNTED_NOTE, unaccountedPieces, unaccountedRows } from './orderPlan';
import { C, MONO, RADIUS } from './tokens';
import { RestockV2Order, RestockV2ThumbTarget, RestockV2Unaccounted } from './types';

// 「這些沒有進到任何清單」 — the coverage ledger's output
// (backend noke-restock-v2-reconcile.js). Empty on every healthy plan, which is why it renders
// nothing at all when there is nothing to say.
//
// It exists because this feature has silently swallowed real things three times
// (feedback_a_gate_must_never_hide_owned_stock): a gate removed a style from the PLAN and
// nobody noticed it had also removed it from the SCREEN. If a line ever stops reaching 訂貨 /
// 去拿 / 上架 / 帶回 / 等到貨, it now lands here, on the screen where it was looked for, instead
// of disappearing. This is a BUG REPORT, not a work list — nothing here is orderable or
// tickable, and the row says what the plan knows so it can be reported as-is.
const Row: React.FC<{ row: RestockV2Unaccounted; onImage: (target: RestockV2ThumbTarget) => void }> = ({
  row, onImage,
}) => (
  <Box
    component="article" data-unaccounted={row.sku}
    sx={{
      bgcolor: C.card, border: '2px dashed', borderColor: C.back, borderRadius: `${RADIUS}px`,
      p: 1.1, mb: 0.9,
    }}
  >
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <ProductThumb
        target={thumbTarget({ sku: row.sku, title: row.title || row.sku, image_url: row.image_url ?? null })}
        width={64} height={85} radius={7} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <SeasonBadge value={row.season} />
          <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>
          {row.on_list && (
            <Typography component="span" sx={{ fontSize: 11, color: C.back, fontWeight: 800 }}>在選品清單上</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
          {displayTitle(row.title || row.sku)}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center', mt: '2px' }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
          {!!row.color && (
            <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.color} {row.size}</Typography>
          )}
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>
            目標 {row.target} · 在櫃 {row.on_hand} · 辦公室 {row.office}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: C.back, fontWeight: 700, mt: '3px' }}>{row.reason}</Typography>
      </Box>
      <Box aria-label={`少了 ${row.pieces} 件`} sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>少了</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.1 }}>
          {row.pieces}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
      </Box>
    </Box>
  </Box>
);

const UnaccountedSection: React.FC<{
  order: RestockV2Order | undefined; onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ order, onImage }) => {
  const rows = unaccountedRows(order);
  if (!rows.length) return null;
  const pieces = unaccountedPieces(order);
  return (
    <Box component="section" aria-label={`沒有進到任何清單 ${pieces} 件`} data-unaccounted-section="" sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.5 }}>
        <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em', color: C.back }}>
          這些沒有進到任何清單
          <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>
            {rows.length} 款 · 請回報
          </Box>
        </Typography>
        <Typography
          sx={{
            ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.back,
            bgcolor: C.backWash, borderRadius: '99px', px: 1,
          }}
        >
          {pieces} 件
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>{UNACCOUNTED_NOTE}</Typography>
      {rows.map((row) => <Row key={`${row.sku}:${row.variant_id ?? 'sku'}`} row={row} onImage={onImage} />)}
    </Box>
  );
};

export default UnaccountedSection;
